/**
 * SublimeCare - CSS Logo Component
 *
 * A stylized logo for SublimeCare tenant using pure CSS/SVG
 * Healthcare/wellness theme with teal colors
 */

interface SublimeCareLogoProps {
  size?: 'sm' | 'md' | 'lg';
  showText?: boolean;
  className?: string;
}

export function SublimeCareLogo({ size = 'md', showText = true, className = '' }: SublimeCareLogoProps) {
  const sizeClasses = {
    sm: { icon: 'w-8 h-8', text: 'text-sm', container: 'gap-2' },
    md: { icon: 'w-10 h-10', text: 'text-base', container: 'gap-2' },
    lg: { icon: 'w-14 h-14', text: 'text-xl', container: 'gap-3' },
  };

  const s = sizeClasses[size];

  return (
    <div className={`flex items-center ${s.container} ${className}`}>
      {/* Icon - Heart with care hands */}
      <div
        className={`${s.icon} relative flex items-center justify-center rounded-lg`}
        style={{
          background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 50%, #2dd4bf 100%)',
          boxShadow: '0 4px 6px -1px rgba(13, 148, 136, 0.3), 0 2px 4px -1px rgba(13, 148, 136, 0.2)',
        }}
      >
        {/* Healthcare icon with heart and hands */}
        <svg
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-full p-1.5"
        >
          {/* Background circle */}
          <circle cx="20" cy="20" r="16" fill="white" fillOpacity="0.95" />

          {/* Heart shape */}
          <path
            d="M20 28C20 28 10 22 10 16C10 13 12.5 11 15 11C17 11 19 12.5 20 14C21 12.5 23 11 25 11C27.5 11 30 13 30 16C30 22 20 28 20 28Z"
            fill="#0d9488"
          />

          {/* Plus sign inside heart */}
          <rect x="18" y="14" width="4" height="10" rx="1" fill="white" />
          <rect x="15" y="17" width="10" height="4" rx="1" fill="white" />

          {/* Caring hands at bottom */}
          <path
            d="M8 30C8 30 10 28 12 28C14 28 15 29 15 30"
            stroke="#14b8a6"
            strokeWidth="2"
            strokeLinecap="round"
            fill="none"
          />
          <path
            d="M32 30C32 30 30 28 28 28C26 28 25 29 25 30"
            stroke="#14b8a6"
            strokeWidth="2"
            strokeLinecap="round"
            fill="none"
          />
        </svg>

        {/* Shine effect */}
        <div
          className="absolute inset-0 rounded-lg opacity-30"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%)',
          }}
        />
      </div>

      {/* Text */}
      {showText && (
        <div className="flex flex-col leading-tight">
          <span
            className={`font-bold tracking-tight ${s.text}`}
            style={{ color: '#0d9488' }}
          >
            Sublime
          </span>
          <span
            className="text-xs text-slate-500 font-medium -mt-0.5"
          >
            Care
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * SublimeCare Logo as a simple icon (no text)
 */
export function SublimeCareLogoIcon({ size = 'md', className = '' }: Omit<SublimeCareLogoProps, 'showText'>) {
  return <SublimeCareLogo size={size} showText={false} className={className} />;
}

export default SublimeCareLogo;
